import { amplifyConfigured } from '@/lib/amplify-ready';

/** Typed models ship with `amplify_outputs.json`; loosen here so the web package builds before sandbox. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnyDataClient = any;

/** Public catalog models (allow.publicApiKey) use the API key when the user is not signed in. */
export const publicApiAuth = { authMode: 'apiKey' as const };

let publicClientPromise: Promise<AnyDataClient> | null = null;
let authedClientPromise: Promise<AnyDataClient> | null = null;

function clientHasModels(client: AnyDataClient): boolean {
  return Boolean(client?.models?.Vendor?.list && client?.models?.Product?.list);
}

/** Throws if generateClient ran before Amplify.configure(outputs with model_introspection). */
export async function assertDataClientReady(): Promise<void> {
  const { generateClient } = await import('aws-amplify/data');
  const probe = generateClient({ authMode: 'apiKey' });
  if (!clientHasModels(probe)) {
    throw new Error('Amplify data client missing model introspection');
  }
}

async function waitForAmplifyConfigure(): Promise<void> {
  await amplifyConfigured;
}

async function createPublicClient(): Promise<AnyDataClient> {
  const { generateClient } = await import('aws-amplify/data');
  const client = generateClient({ authMode: 'apiKey' });
  if (!clientHasModels(client)) {
    throw new Error('Amplify data client missing model introspection');
  }
  return client;
}

async function createAuthedClient(): Promise<AnyDataClient> {
  const { generateClient } = await import('aws-amplify/data');
  const client = generateClient({ authMode: 'userPool' });
  if (!clientHasModels(client)) {
    throw new Error('Amplify data client missing model introspection');
  }
  return client;
}

/** Clear cached clients — call after sign-out so the next request does not reuse userPool. */
export function resetDataClient() {
  publicClientPromise = null;
  authedClientPromise = null;
}

/** Prefer Cognito user pool when signed in — API key guest reads when signed out. */
export async function resolveCatalogAuth(): Promise<
  { authMode: 'userPool' } | { authMode: 'apiKey' }
> {
  try {
    const { fetchAuthSession } = await import('aws-amplify/auth');
    const session = await fetchAuthSession();
    if (session.tokens?.idToken ?? session.tokens?.accessToken) {
      return { authMode: 'userPool' };
    }
  } catch {
    /* not signed in */
  }
  return publicApiAuth;
}

/** AppSync client for public catalog reads (Product, Vendor, Classified, Category, …). */
export async function getPublicDataClient(): Promise<AnyDataClient> {
  await waitForAmplifyConfigure();
  if (!publicClientPromise) {
    publicClientPromise = createPublicClient();
  }
  return publicClientPromise;
}

/** AppSync client for signed-in owner / admin mutations. */
export async function getAuthedDataClient(): Promise<AnyDataClient> {
  await waitForAmplifyConfigure();
  if (!authedClientPromise) {
    authedClientPromise = createAuthedClient();
  }
  return authedClientPromise;
}

/** Pick the correct client for marketplace browsing (guests and signed-in users). */
export async function getCatalogClient(): Promise<AnyDataClient> {
  const auth = await resolveCatalogAuth();
  return auth.authMode === 'userPool' ? getAuthedDataClient() : getPublicDataClient();
}

/** @deprecated Prefer getCatalogClient for reads or getAuthedDataClient for writes. */
export async function getDataClient(): Promise<AnyDataClient> {
  return getAuthedDataClient();
}
